import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import Logo from '../components/ui/Logo';
import GanttTimeline from '../components/pm/GanttTimeline';
import LoadingScreen from '../components/ui/LoadingScreen';
import { onboardingItemToProjectTask } from '../utils/onboardingTaskBridge';
import type { ProjectTask, TaskGroup } from '../types';
import './ProjectTimelineDisplayPage.css';

/**
 * Full-bleed project timeline for wall / TV: no hub chrome, no task sidebar, read-only chart.
 */
const ProjectTimelineDisplayPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    timberProjects,
    projectTasks,
    taskGroups,
    projectOnboardings,
    milestones,
    crmUsers,
    loading,
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings', 'milestones']);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const currentProject = useMemo(
    () => (projectId ? timberProjects.find((p) => p.id === projectId) : null),
    [projectId, timberProjects],
  );

  const onboarding = useMemo(() => {
    if (!projectId) return undefined;
    return projectOnboardings.find((o) => o.projectId === projectId);
  }, [projectId, projectOnboardings]);

  const filteredGroups: TaskGroup[] = useMemo(() => {
    if (!projectId) return [];
    return taskGroups.filter((g) => g.projectId === projectId);
  }, [taskGroups, projectId]);

  const onboardingMirror: ProjectTask[] = useMemo(() => {
    if (!projectId || !onboarding?.items?.length) return [];
    return onboarding.items.map((it) => onboardingItemToProjectTask(it, projectId, filteredGroups));
  }, [projectId, onboarding, filteredGroups]);

  const filteredTasks = useMemo(() => {
    if (!projectId) return [];
    return projectTasks.filter((t) => t.projectId === projectId);
  }, [projectTasks, projectId]);

  const tasksForViews = useMemo(
    () => [...onboardingMirror, ...filteredTasks],
    [onboardingMirror, filteredTasks],
  );

  const projectMilestones = useMemo(() => {
    if (!projectId) return [];
    return milestones.filter((m) => m.projectId === projectId);
  }, [milestones, projectId]);

  const noopUpdate = useCallback(async (_id: string, _u: Partial<ProjectTask>) => {}, []);

  if (!projectId) {
    return <Navigate to="/project-hub" replace />;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentProject?.id) {
    return (
      <div className="project-timeline-display project-timeline-display--missing">
        <p>Project not found.</p>
        <Link to="/project-hub" className="project-timeline-display-link">
          Back to Project Hub
        </Link>
      </div>
    );
  }

  const timeLabel = now.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const phaseLabel =
    currentProject.phase === 'preconstruction'
      ? 'Preconstruction'
      : currentProject.phase === 'contracted'
        ? 'Under contract'
        : '';

  return (
    <div className="project-timeline-display">
      <header className="project-timeline-display-header">
        <div className="project-timeline-display-brand">
          <div className="project-timeline-display-logo-wrap">
            <Logo size={40} />
          </div>
          <div>
            <h1 className="project-timeline-display-title">{currentProject.name}</h1>
            <p className="project-timeline-display-meta">
              {phaseLabel && <span>{phaseLabel}</span>}
              {currentProject.clientName && (
                <>
                  {phaseLabel ? ' · ' : ''}
                  <span>{currentProject.clientName}</span>
                </>
              )}
            </p>
            <p className="project-timeline-display-tagline">Timeline — display mode</p>
          </div>
        </div>
        <div className="project-timeline-display-header-right">
          <time className="project-timeline-display-clock" dateTime={now.toISOString()}>
            {timeLabel}
          </time>
          <Link to={`/project-hub/${projectId}`} className="project-timeline-display-link">
            <LayoutGrid size={16} aria-hidden />
            Project Hub
          </Link>
        </div>
      </header>

      <section className="project-timeline-display-chart" aria-label="Project timeline">
        <GanttTimeline
          tasks={tasksForViews}
          groups={filteredGroups}
          milestones={projectMilestones}
          proposalSignedAt={null}
          projectPhase={currentProject.phase ?? null}
          teamMembers={crmUsers}
          onUpdateTask={noopUpdate}
          onSelectTask={() => {}}
          hideLeftPanel
          displayMode
          initialZoom="fit"
          showCriticalPathOverlay={false}
        />
      </section>
    </div>
  );
};

export default ProjectTimelineDisplayPage;
